import React, { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup, FormControl, FormLabel, Typography, Box, Grid, IconButton, Chip, Button } from '@mui/material';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { productFiltersAtom } from '../../../Services/_states';
import { ECommerceActions } from '../../../Services/_actions/ECommerce/ecommerce.action';
import DeleteIcon from '@mui/icons-material/Delete'; // Import the Delete icon from MUI
interface Filter {
  value: string;
  type: string;
  label?: string;
  objId?: string | number;
}

interface Category {
  p_set_id: string;
  name: string;
}

const priceStatuses = [
  { value: '>1000', type: 'price' },
  { value: '1000-5000', type: 'price' },
  { value: '5000-10000', type: 'price' },
  { value: '<10000', type: 'price' },
];

const AvailabilityValues = [
  { label: 'In stock', value: 'in stock' },
  { label: 'Out of stock', value: 'out of stock' },
  { label: 'Pre-order', value: 'preorder' },
  { label: 'Discontinued', value: 'discontinued' },
];

const ProductFilters: React.FC = () => {
  const actions = ECommerceActions();
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);
  const [categoryFilterList, setCategoryFilterList] = useState<Category[]>([]);
  const [filterObj, setFilterObj] = useRecoilState<any>(productFiltersAtom);
  const resetFilterObj = useResetRecoilState(productFiltersAtom);

  const getProductItemList = async (data: object) => {
    await actions.getProductItemList(data);
    resetFilterObj();
  };

  const handleCheckboxChange = (filter: Filter) => {
    setSelectedFilters((prev) => {
      const isSelected = prev.some(item => item.value === filter.value && item.type === filter.type);
      if (isSelected) {
        return prev.filter(item => !(item.value === filter.value && item.type === filter.type));
      } else {
        return [...prev, filter];
      }
    });
  };

  const handleCheckCategory = (filter: Category | any, type = 'category', keyName = 'value', labelKey = "value") => {
    filter.type = type;
    filter.objId = filter[keyName];
    filter.label = filter[labelKey];
    setSelectedFilters((prev) => {
      const isSelected = prev.some(item => item?.objId === filter?.objId && item.type === filter.type);
      if (isSelected) {
        return prev.filter(item => !(item.objId === filter.objId && item.type === filter.type));
      } else {
        return [...prev, filter];
      }
    });
  };

  const removeFilter = (filter: Filter) => {
    setSelectedFilters((prev) => prev.filter(item => item.objId !== filter.objId || item.type !== filter.type));
  };

  const getBadges = () => {
    return selectedFilters?.map((filter, index) => (
      <Box
        key={index}
        sx={{
          backgroundColor: '#e0ebeb',
          borderRadius: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          marginRight: '5px',
        }}
      >
        <IconButton
          sx={{ marginRight: '5px', padding: 0 }}
          onClick={() => removeFilter(filter)}
        >
          <DeleteIcon />
        </IconButton>
        {filter?.label}
      </Box>
    ));
  };

  const getProductSetsList = async () => {
    const res = await actions.getProductSetsList();
    setCategoryFilterList(res?.list || []);
  };

  useEffect(() => {
    getProductSetsList();
  }, []);

  useEffect(() => {
    const result: any = selectedFilters?.reduce((acc, item: any) => {
      if (!acc[item.type]) {
        acc[item?.type] = [];
      }
      acc[item?.type].push(item?.objId);
      return acc;
    }, {} as Record<string, string[]>);

    for (const key in result)
      result[key] = result[key]?.join(',');

    let filterToApply = {};
    if (Object.keys(result).length > 0) {
      filterToApply = { ...filterObj, ...result, page: 0, search: '' };
    } else {
      filterToApply = { ...filterObj, page: 0, search: '', category: '', availability: '' };
    }

    setFilterObj(filterToApply);
    getProductItemList(filterToApply);
  }, [selectedFilters]);

  const clearAllFilters = () => {
    resetFilterObj();
    setSelectedFilters([]);
  };


  return (
    <Box sx={{ maxHeight: 'calc(100vh - 125px)', width: '100%', overflowY: 'auto', scrollbarWidth: 'none', 
      '&::-webkit-scrollbar': {
        display: 'none',
      }, }}>
      {/* <Typography variant="h5" component="strong" sx={{ display: 'block' }}>Product</Typography> */}
      <Box className="d-flex justify-content-between align-items-center">
      <Typography variant="h6" component="strong" sx={{ display: 'block' }}>Product</Typography>
        {(selectedFilters?.length > 0) &&
          <Button variant="outlined" onClick={clearAllFilters}>
            Clear
          </Button>
        }
      </Box>

      <Typography variant="body1" sx={{ display: 'block', marginTop: 1, marginBottom: 2 }}>
        {/* Filters: {getBadges()} */}
        Filters: {selectedFilters?.map((filter, index) => (
          <Chip
            key={index}
            label={filter.label}
            onDelete={() => removeFilter(filter)}
            variant="outlined"
            color="primary"
            style={{ margin: '5px' }}
          />
        ))}
      </Typography>
      <FormControl component="fieldset" sx={{ mt: 1 }}>
        <FormLabel component="legend">Category</FormLabel>
        <hr style={{ margin: '0 0 10px' }} />
        <FormGroup>
          <Grid container direction="column">
            {categoryFilterList?.map((status) => (
              <Grid item key={status?.p_set_id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFilters?.some(item => item?.objId === status.p_set_id && item?.type === 'category')}
                      onChange={() => handleCheckCategory(status, 'category', 'p_set_id', 'name')}
                    />
                  }
                  label={status?.name}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>

        <FormLabel component="legend">Price Bar</FormLabel>
        <hr style={{ margin: '0 0 10px' }} />
        <FormGroup>
          <Grid container direction="column">
            {priceStatuses.map((status) => (
              <Grid item key={status.value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFilters?.some(item => item.value === status.value && item.type === status.type)}
                      onChange={() => handleCheckboxChange(status)}
                    />
                  }
                  label={status.value}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>

        <FormLabel component="legend">Stock Availability</FormLabel>
        <hr style={{ margin: '0 0 10px' }} />
        <FormGroup>
          <Grid container direction="column">
            {AvailabilityValues?.map((availability: any, index) => (
              <Grid item key={index}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFilters.some(item => item?.objId === availability.value && item?.type === 'availability')}
                      onChange={() => handleCheckCategory(availability, 'availability', 'value', 'label')}
                    />
                  }
                  label={availability?.label}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </FormControl>
    </Box>
  );
};

export default ProductFilters;
